import React, { useId } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { INK } from './Type';
type Node = {
  x: number;
  y: number;
  r: number;
  c: string;
  ring: number;
};
const RINGS = [{
  radius: 26,
  count: 6,
  r: 1.9
}, {
  radius: 48,
  count: 12,
  r: 1.5
}, {
  radius: 72,
  count: 20,
  r: 1.15
}];
const TONES = [INK.blue, INK.emerald, INK.violet, INK.gold];
function buildNodes(): Node[] {
  const nodes: Node[] = [];
  RINGS.forEach((ring, ri) => {
    for (let i = 0; i < ring.count; i += 1) {
      const angle = i / ring.count * Math.PI * 2 + ri * 0.42;
      // Slight deterministic wobble so the graph reads organic, not clockwork.
      const wobble = 1 + 0.075 * Math.sin(i * 2.3 + ri);
      nodes.push({
        x: 100 + Math.cos(angle) * ring.radius * wobble,
        y: 100 + Math.sin(angle) * ring.radius * wobble * 0.86,
        r: ring.r,
        c: TONES[(i + ri) % TONES.length],
        ring: ri
      });
    }
  });
  return nodes;
}
const NODES = buildNodes();

/**
 * The Venture Intelligence Brain — an abstract knowledge graph of entities,
 * capital flows and outcomes converging on a luminous core. `density` (0–1)
 * controls how connected the graph appears, so the visual can grow more
 * connected as the deck progresses.
 */
export function BrainGraph({
  density = 1,
  className
}: {
  density?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  const id = useId().replace(/:/g, '');
  const inner = NODES.filter(n => n.ring === 0);
  const mid = NODES.filter(n => n.ring === 1);
  const outer = NODES.filter(n => n.ring === 2);
  const link = (a: Node, b: Node, key: string, o: number) => <line key={key} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#7FB0FF" strokeWidth={0.35} opacity={o} data-id="element-4" data-mp="36x4xipa3xe7e" />;
  return <svg viewBox="0 0 200 200" className={className} role="img" aria-label="Abstract venture knowledge graph converging on a luminous intelligence core" data-id="element-5" data-mp="m41hh7ch7p8u">
      <defs data-id="element-6" data-mp="2n5zlmd71tshz">
        <radialGradient id={`core-${id}`} data-id="element-7" data-mp="2n5pw0qpx1nct">
          <stop offset="0%" stopColor="#DCEBFF" stopOpacity="0.95" data-id="element-8" data-mp="2i7y07lm5hh3c" />
          <stop offset="35%" stopColor="#3B8CFF" stopOpacity="0.55" data-id="element-9" data-mp="2i7obs2gr7hzr" />
          <stop offset="100%" stopColor="#3B8CFF" stopOpacity="0" data-id="element-10" data-mp="2w6o0190ctuve" />
        </radialGradient>
      </defs>

      {/* Sphere latitude hints */}
      {[0, 1, 2].map(i => <ellipse key={i} cx={100} cy={100} rx={72} ry={26 + i * 22} fill="none" stroke="#FFFFFF" strokeWidth={0.3} opacity={0.07} data-id="element-11" data-mp="23is260z1twx1" />)}
      <circle cx={100} cy={100} r={72} fill="none" stroke="#FFFFFF" strokeWidth={0.3} opacity={0.09} data-id="element-12" data-mp="2d9mot6g7iyj0" />

      {/* Core → inner ring */}
      {inner.map((n, i) => link({
      x: 100,
      y: 100,
      r: 0,
      c: '',
      ring: -1
    }, n, `c${i}`, 0.5))}
      {/* Inner → mid */}
      {mid.map((n, i) => link(inner[i % inner.length], n, `m${i}`, 0.3 * density + 0.06))}
      {/* Mid → outer */}
      {outer.map((n, i) => i / outer.length <= density ? link(mid[i % mid.length], n, `o${i}`, 0.24 * density + 0.05) : null)}

      <circle cx={100} cy={100} r={44} fill={`url(#core-${id})`} data-id="element-13" data-mp="3ieb8r372izw9" />
      <motion.circle cx={100} cy={100} r={6.5} fill="#E8F1FF" animate={reduced ? undefined : {
      opacity: [0.85, 1, 0.85]
    }} transition={{
      duration: 3.6,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-14" data-mp="3ng0j0gmgv9ja" />

      {NODES.map((n, i) => <circle key={i} cx={n.x} cy={n.y} r={n.r} fill={n.c} opacity={n.ring === 2 ? 0.55 + 0.35 * density : 0.9} data-id="element-15" data-mp="5217m44qxs6v" data-mpi={i} />)}
    </svg>;
}